import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Card, TextField, MenuItem, Select, OutlinedInput, Checkbox, ListItemText, Chip, FormControl, InputLabel,
    Button, Grid, LinearProgress, Alert, Snackbar, Stack, Typography, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
    Collapse, IconButton, Tooltip, CircularProgress, FormControlLabel
} from "@mui/material";
import {
    PlayCircleOutline as RunIcon,
    SaveOutlined as SaveIcon,
    UploadFile as UploadIcon,
    FileDownload as DownloadIcon,
    VisibilityOutlined as ViewIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    ViewColumn as ViewColumnIcon,
    RestartAlt as RestartAltIcon,
    FilterList as FilterListIcon,
    TableRows as DensityIcon,
    ErrorOutline as ErrorIcon,
    WarningAmberOutlined as WarningIcon,
    CheckCircleOutline as PassedIcon,
    PeopleAltOutlined as PeopleIcon,
    PaymentsOutlined as PaymentsIcon,
    RemoveCircleOutline as DeductionIcon,
    AccountBalanceWalletOutlined as NetPayIcon,
    LockOutlined as LockIcon
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';
import { useNavigate } from 'react-router-dom';
import EffectiveDatedPayrollBreakdown from '../../components/payroll/EffectiveDatedPayrollBreakdown';

const PAYROLL_EXECUTION_GRID_KEY = "payroll-execution";

const CATEGORY_DEFINITIONS = [
    {
        id: 'staff',
        title: 'Staff Information',
        colIds: ['employee_number', 'full_name']
    },
    {
        id: 'base',
        title: 'Base Compensation',
        colIds: ['basic_salary', 'hourly_rate']
    },
    {
        id: 'ot',
        title: 'Overtime & Hours',
        colIds: [
            'payable_ot_mins', 'ot_pay',
            'normal_ot_mins', 'normal_ot_pay',
            'rest_ot_mins', 'rest_ot_pay',
            'holiday_ot_mins', 'holiday_ot_pay',
            'extended_ot_mins', 'extended_ot_pay'
        ]
    },
    {
        id: 'allowances',
        title: 'Allowances',
        colIds: ['meal_allowance', 'transport_allowance', 'special_allowance', 'ot_allowance'],
        matchDynamic: colId => colId.startsWith('alw_')
    },
    {
        id: 'deductions',
        title: 'Deductions',
        colIds: ['late_early_penalties', 'ssb_deduction', 'tax_deduction', 'unpaid_leave_deduction', 'unpaid_absence_deduction'],
        matchDynamic: colId => colId.startsWith('ded_')
    },
    {
        id: 'result',
        title: 'Payroll Summary',
        colIds: ['gross_pay', 'net_pay']
    }
];

const buildColumnLabels = (colDefs = []) => {
    const labels = {};
    const traverse = (defs) => {
        defs.forEach(col => {
            if (col.children) {
                traverse(col.children);
            } else {
                const colId = col.colId || col.field;
                if (colId) {
                    labels[colId] = col.headerName || col.field || colId;
                }
            }
        });
    };
    traverse(colDefs);
    return labels;
};

const money = value => Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
});

const SummaryMetric = ({ icon: Icon, label, value, tone = 'neutral', helper }) => {
    const tones = {
        neutral: { bgcolor: '#f8fafc', iconBg: '#e2e8f0', iconColor: '#475569', valueColor: '#0f172a' },
        primary: { bgcolor: '#eef2ff', iconBg: '#e0e7ff', iconColor: '#3730a3', valueColor: '#312e81' },
        success: { bgcolor: '#f0fdf4', iconBg: '#dcfce7', iconColor: '#15803d', valueColor: '#166534' },
        danger: { bgcolor: '#fef2f2', iconBg: '#fee2e2', iconColor: '#b91c1c', valueColor: '#991b1b' }
    };
    const colors = tones[tone] || tones.neutral;

    return (
        <Box sx={{ px: 1.5, py: 1.1, borderRadius: 2.5, border: '1px solid #e2e8f0', bgcolor: colors.bgcolor, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ width: 30, height: 30, borderRadius: 1.75, bgcolor: colors.iconBg, color: colors.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon sx={{ fontSize: 17 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 800, fontSize: '0.68rem', lineHeight: 1.1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</Typography>
                    <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ mt: 0.25, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ color: colors.valueColor, fontWeight: 900, fontSize: '1.05rem', lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Typography>
                        {helper && <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.68rem', lineHeight: 1.1, whiteSpace: 'nowrap' }}>{helper}</Typography>}
                    </Stack>
                </Box>
            </Stack>
        </Box>
    );
};

const ReadinessGroup = ({ title, checks, severity, onOpenDetails }) => {
    const config = {
        ERROR: { icon: ErrorIcon, color: '#b91c1c', bgcolor: '#fef2f2', border: '#fecaca', empty: 'No blocking issues' },
        WARNING: { icon: WarningIcon, color: '#92400e', bgcolor: '#fffbeb', border: '#fde68a', empty: 'No warnings' },
        PASS: { icon: PassedIcon, color: '#166534', bgcolor: '#f0fdf4', border: '#bbf7d0', empty: 'No completed checks' }
    }[severity];
    const Icon = config.icon;

    return (
        <Box sx={{ p: 1.75, borderRadius: 2.5, border: `1px solid ${config.border}`, bgcolor: config.bgcolor, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                <Icon sx={{ color: config.color, fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ color: config.color, fontWeight: 900 }}>{title}</Typography>
                <Chip label={checks.length} size="small" sx={{ ml: 'auto', height: 22, bgcolor: '#fff', color: config.color, fontWeight: 900 }} />
            </Stack>
            <Stack
                spacing={0.75}
                sx={{
                    maxHeight: 220,
                    overflowY: 'auto',
                    pr: 0.5,
                    scrollbarGutter: 'stable',
                    '&::-webkit-scrollbar': { width: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(100, 116, 139, 0.35)', borderRadius: 999 }
                }}
            >
                {checks.length > 0 ? checks.map(check => {
                    const hasDetails = check.details && check.details.length > 0;
                    return (
                        <Button
                            key={check.code}
                            onClick={hasDetails ? () => onOpenDetails(check) : undefined}
                            disabled={!hasDetails}
                            sx={{ justifyContent: 'flex-start', textAlign: 'left', textTransform: 'none', color: '#334155', fontWeight: 650, fontSize: '0.78rem', lineHeight: 1.35, px: 1, py: 0.75, bgcolor: '#fff', border: '1px solid rgba(148,163,184,0.24)', '&.Mui-disabled': { color: '#334155', opacity: 1 }, '&:hover': { bgcolor: '#fff' } }}
                        >
                            {check.message}{hasDetails ? ' →' : ''}
                        </Button>
                    );
                }) : <Typography variant="caption" sx={{ color: config.color, fontWeight: 700 }}>{config.empty}</Typography>}
            </Stack>
        </Box>
    );
};

const PayrollProcessing = ({ setIsLoggedIn }) => {
    const navigate = useNavigate();
    const gridRef = useRef();
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        run_type: 'REGULAR',
        period_type: 'REGULAR',
        payroll_group_id: '',
        advance_schedule_id: '',
        off_cycle_elements: []
    });
    const [viewFilters, setViewFilters] = useState({ searchText: '', orgNodeId: '', branchId: '', gradeId: '' });
    const [metadata, setMetadata] = useState({ nodes: [], branches: [], grades: [], payrollGroups: [] });
    const [elements, setElements] = React.useState([]);
    const [existingRun, setExistingRun] = useState(null);
    const [readiness, setReadiness] = useState(null);
    const [statusMsg, setStatusMsg] = useState({ open: false, msg: '', severity: 'success' });
    const [detailsDialog, setDetailsDialog] = useState({ open: false, title: '', details: [] });
    const [breakdownPayroll, setBreakdownPayroll] = useState(null);

    // Accordion visibility states
    const [accState, setAccState] = useState(() => {
        try {
            const saved = localStorage.getItem('payroll_acc_state');
            return saved ? JSON.parse(saved) : { params: true, readiness: true, filters: true };
        } catch {
            return { params: true, readiness: true, filters: true };
        }
    });

    const toggleAccordion = (section) => {
        setAccState(prev => {
            const next = { ...prev, [section]: !prev[section] };
            try { localStorage.setItem('payroll_acc_state', JSON.stringify(next)); } catch {}
            return next;
        });
    };

    // Column visibility preferences states
    const columnLabelsRef = useRef({});
    const [columnDialogOpen, setColumnDialogOpen] = useState(false);
    const [groupedColumns, setGroupedColumns] = useState([]);
    const [gridPreferenceLoading, setGridPreferenceLoading] = useState(false);
    const [gridPreferenceSaving, setGridPreferenceSaving] = useState(false);
    const [rowDensity, setRowDensity] = useState(() => localStorage.getItem('payroll_row_density') || 'compact');


    const months = [
        { value: 1, label: 'January' }, { value: 2, label: 'February' },
        { value: 3, label: 'March' }, { value: 4, label: 'April' },
        { value: 5, label: 'May' }, { value: 6, label: 'June' },
        { value: 7, label: 'July' }, { value: 8, label: 'August' },
        { value: 9, label: 'September' }, { value: 10, label: 'October' },
        { value: 11, label: 'November' }, { value: 12, label: 'December' }
    ];

    const years = [2025, 2026, 2027];

    React.useEffect(() => {
        const fetchElementsAndMetadata = async () => {
            try {
                const token = localStorage.getItem('token');
                const tenant = localStorage.getItem('tenant') || '';
                const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };

                const [elementsRes, nodesRes, branchesRes, gradesRes, groupsRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/elements`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/grades`, { headers, withCredentials: true }),
                    axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll-groups`, { headers, withCredentials: true })
                ]);

                if (elementsRes.data.success) {
                    setElements(elementsRes.data.data.filter(e => e.processing_type === 'OFF_CYCLE' || e.processing_type === 'BOTH'));
                }
                setMetadata({
                    nodes: nodesRes.data?.data || [],
                    branches: branchesRes.data?.data || [],
                    grades: gradesRes.data?.data || [],
                    payrollGroups: (groupsRes.data?.data || []).filter(group => Number(group.is_active) === 1)
                });
                const activeGroups = (groupsRes.data?.data || []).filter(group => Number(group.is_active) === 1);
                const defaultGroup = activeGroups.find(group => group.group_code === 'DEFAULT') || activeGroups[0];
                if (defaultGroup) setFilters(prev => prev.payroll_group_id ? prev : { ...prev, payroll_group_id: defaultGroup.id });
            } catch (e) { console.error(e); }
        };
        fetchElementsAndMetadata();
    }, []);

    const filteredRowData = useMemo(() => {
        return rowData.filter(row => {
            if (viewFilters.searchText) {
                const search = viewFilters.searchText.toLowerCase();
                const matchesText = (row.full_name || '').toLowerCase().includes(search) ||
                    (row.employee_number || '').toLowerCase().includes(search) ||
                    (row.staff_code || '').toLowerCase().includes(search);
                if (!matchesText) return false;
            }
            if (viewFilters.orgNodeId && row.org_node_id !== viewFilters.orgNodeId) return false;
            if (viewFilters.branchId && row.branch_id !== viewFilters.branchId) return false;
            if (viewFilters.gradeId && row.grade_id !== viewFilters.gradeId) return false;
            return true;
        });
    }, [rowData, viewFilters]);


    const fetchPreview = useCallback(async () => {
        setLoading(true);
        setExistingRun(null);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const params = { ...filters };
            if (filters.run_type === 'OFF_CYCLE') {
                params.off_cycle_elements = JSON.stringify(filters.off_cycle_elements);
            }
            const readinessResponse = filters.run_type === 'REGULAR' ? await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/readiness`, {
                params: { month: filters.month, year: filters.year, payroll_group_id: filters.payroll_group_id, period_type: filters.period_type, advance_schedule_id: filters.advance_schedule_id || undefined },
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true
            }) : null;
            setReadiness(readinessResponse?.data?.data || null);
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/preview`, {
                params: params,
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setRowData(response.data.data);
                setExistingRun(filters.run_type === 'REGULAR' ? (response.data.existingRun || null) : null);
            }
        } catch (error) {
            console.error("Error fetching preview:", error);
            setStatusMsg({ open: true, msg: 'Error calculating preview', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const changePeriodLock = async (lock) => {
        const tenant = localStorage.getItem('tenant') || '';
        const token = localStorage.getItem('token');
        let reason;
        if (!lock) {
            reason = window.prompt('Reason for reopening this payroll period:') || '';
            if (!reason.trim()) return;
        }
        setLoading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/period/${lock ? 'lock' : 'reopen'}`, { ...filters, reason }, { headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true });
            const response = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/payroll/readiness`, { params: filters, headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant }, withCredentials: true });
            setReadiness(response.data.data);
            setStatusMsg({ open: true, msg: lock ? 'Payroll period locked' : 'Payroll period reopened', severity: 'success' });
        } catch (error) {
            setStatusMsg({ open: true, msg: error.response?.data?.message || 'Could not update payroll period', severity: 'error' });
        } finally { setLoading(false); }
    };

    const createRun = async () => {
        if (filters.run_type === 'REGULAR' && existingRun) return;
        if (!window.confirm("This will save a DRAFT payroll run for this month. Continue?")) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const payload = {
                ...filters,
                payroll_group_id: filters.payroll_group_id || null,
                advance_schedule_id: filters.advance_schedule_id || null,
            };
            const response = await axios.post(`${process.env.REACT_APP_API_URL1}/sm/payroll/runs`, payload, {
                headers: { Authorization: `Bearer ${token}`, 'X-Tenant': tenant },
                withCredentials: true
            });
            if (response.data.success) {
                setStatusMsg({ open: true, msg: 'Payroll run saved as DRAFT', severity: 'success' });
                if (filters.run_type === 'REGULAR') {
                    setExistingRun({ id: response.data.runId, status: 'DRAFT', period_month: filters.month, period_year: filters.year });
                }
            }
        } catch (error) {
            console.error("Error saving run:", error);
            setStatusMsg({ open: true, msg: 'Error saving payroll run', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const onBtnExport = useCallback(() => {
        if (gridRef.current?.api) {
            const api = gridRef.current.api;
            const columnKeys = api.getColumnState?.()
                ? api.getColumnState().filter(c => !c.hide && c.colId !== 'audit').map(c => c.colId)
                : undefined;
            api.exportDataAsCsv({
                fileName: `payroll_preview_${filters.year}_${filters.month}_${filters.run_type}.csv`,
                columnKeys
            });
        }
    }, [filters]);



    const columnDefs = useMemo(() => {
        const dynamicAlwCols = new Set();
        const dynamicDedCols = new Set();

        (rowData || []).forEach(row => {
            Object.keys(row).forEach(k => {
                if (k.startsWith('alw_')) dynamicAlwCols.add(k);
                if (k.startsWith('ded_')) dynamicDedCols.add(k);
            });
        });

        const dynamicAdditions = Array.from(dynamicAlwCols).map(k => ({
            field: k,
            headerName: k.replace('alw_', ''),
            width: 120,
            valueFormatter: p => p.value?.toLocaleString(),
            cellStyle: { color: '#2e7d32' }
        }));

        const dynamicDeductions = Array.from(dynamicDedCols).map(k => {
            const raw = k.replace('ded_', '');
            const headerName = raw === 'MISSED_CLOCK_PENALTY' ? 'Missed Clock Fine' : raw;
            return {
                field: k,
                headerName,
                width: 140,
                valueFormatter: p => p.value?.toLocaleString(),
                cellStyle: { color: '#d32f2f' }
            };
        });

        return [
            {
                headerName: "Audit",
                colId: "audit",
                pinned: 'left',
                width: 88,
                sortable: false,
                filter: false,
                cellRenderer: p => (
                    <Button
                        size="small"
                        startIcon={<ViewIcon sx={{ fontSize: '1rem !important' }} />}
                        onClick={() => setBreakdownPayroll(p.data)}
                        sx={{
                            minHeight: 24,
                            height: 24,
                            py: 0,
                            px: 1,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                            lineHeight: 1
                        }}
                    >
                        View
                    </Button>
                )
            },
            {
                headerName: "Staff",
                pinned: 'left',
                children: [
                    {
                        field: "employee_number",
                        headerName: "Employee ID",
                        width: 140,
                        valueGetter: p => p.data?.employee_number || p.data?.staff_code || '-'
                    },
                    { field: "full_name", headerName: "Name", width: 180 },
                ]
            },
            {
                headerName: "Base Compensation",
                children: [
                    { field: "basic_salary", headerName: "Basic Salary", width: 125, valueFormatter: p => p.value?.toLocaleString() },
                    { field: "hourly_rate", headerName: "Hourly Rate", width: 110, valueFormatter: p => p.value?.toLocaleString() },
                ]
            },
            {
                headerName: "Additions (OT & Alw)",
                children: [
                    { field: "payable_ot_mins", headerName: "Total OT Minutes", width: 135 },
                    { field: "ot_pay", headerName: "Total OT Pay", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32', fontWeight: 'bold' } },
                    { field: "normal_ot_mins", headerName: "Normal OT Min", width: 120 },
                    { field: "normal_ot_pay", headerName: "Normal OT Pay", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "rest_ot_mins", headerName: "Rest OT Min", width: 110 },
                    { field: "rest_ot_pay", headerName: "Rest OT Pay", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "holiday_ot_mins", headerName: "Holiday OT Min", width: 120 },
                    { field: "holiday_ot_pay", headerName: "Holiday OT Pay", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "extended_ot_mins", headerName: "Extended OT Min", width: 130 },
                    { field: "extended_ot_pay", headerName: "Extended OT Pay", width: 130, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "meal_allowance", headerName: "Meal Allowance", width: 125, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "transport_allowance", headerName: "Transport Allowance", width: 150, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    { field: "special_allowance", headerName: "Special Allowance", width: 135, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#059669' } },
                    { field: "ot_allowance", headerName: "Shift OT Allowance", width: 145, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#2e7d32' } },
                    ...dynamicAdditions
                ]
            },
            {
                headerName: "Deductions",
                children: [
                    { field: "late_early_penalties", headerName: "Late/Early", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "ssb_deduction", headerName: "SSB", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "tax_deduction", headerName: "PIT (Tax)", width: 110, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "unpaid_leave_deduction", headerName: "Unpaid Leave", width: 120, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    { field: "unpaid_absence_deduction", headerName: "Unpaid Absence", width: 135, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { color: '#d32f2f' } },
                    ...dynamicDeductions
                ]
            },
            {
                headerName: "Result",
                children: [
                    { field: "gross_pay", headerName: "Gross Pay", width: 130, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { fontWeight: 'bold' } },
                    { field: "net_pay", headerName: "Net Payable", pinned: 'right', width: 145, valueFormatter: p => p.value?.toLocaleString(), cellStyle: { fontWeight: 'bold', color: '#1a237e', backgroundColor: '#eef2ff' } },
                ]
            }
        ];
    }, [rowData]);

    // Refresh categorized column options based on current grid state
    const refreshColumnOptions = useCallback(() => {
        const api = gridRef.current?.api;
        if (!api?.getColumnState) return;
        const labels = columnLabelsRef.current || {};
        const colStates = api.getColumnState();
        const stateMap = {};
        colStates.forEach(col => {
            stateMap[col.colId] = !col.hide;
        });

        const matchedColIds = new Set();
        const groups = CATEGORY_DEFINITIONS.map(cat => {
            const items = [];
            colStates.forEach(col => {
                if (col.colId === 'audit') return; // Always keep Audit locked/visible
                const isMember = cat.colIds?.includes(col.colId) || (cat.matchDynamic && cat.matchDynamic(col.colId));
                if (isMember) {
                    matchedColIds.add(col.colId);
                    items.push({
                        colId: col.colId,
                        label: labels[col.colId] || col.colId,
                        visible: stateMap[col.colId] !== false
                    });
                }
            });
            return {
                ...cat,
                items
            };
        }).filter(cat => cat.items.length > 0);

        const otherItems = [];
        colStates.forEach(col => {
            if (col.colId === 'audit') return;
            if (!matchedColIds.has(col.colId)) {
                otherItems.push({
                    colId: col.colId,
                    label: labels[col.colId] || col.colId,
                    visible: stateMap[col.colId] !== false
                });
            }
        });
        if (otherItems.length > 0) {
            groups.push({
                id: 'other',
                title: 'Other Columns',
                items: otherItems
            });
        }

        setGroupedColumns(groups);
    }, []);

    useEffect(() => {
        columnLabelsRef.current = buildColumnLabels(columnDefs);
        if (gridRef.current?.api) {
            refreshColumnOptions();
        }
    }, [columnDefs, refreshColumnOptions]);

    const handleToggleColumn = useCallback((colId, visible) => {
        const api = gridRef.current?.api;
        if (!api) return;
        if (api.setColumnsVisible) {
            api.setColumnsVisible([colId], visible);
        } else if (api.setColumnVisible) {
            api.setColumnVisible(colId, visible);
        }
        refreshColumnOptions();
    }, [refreshColumnOptions]);

    const handleToggleGroup = useCallback((groupItems, visible) => {
        const api = gridRef.current?.api;
        if (!api) return;
        const colIds = groupItems.map(item => item.colId);
        if (api.setColumnsVisible) {
            api.setColumnsVisible(colIds, visible);
        }
        refreshColumnOptions();
    }, [refreshColumnOptions]);

    const applyGridPreference = useCallback((gridState) => {
        const api = gridRef.current?.api;
        if (!api || !gridState) return;
        if (Array.isArray(gridState.columnState) && api.applyColumnState) {
            api.applyColumnState({ state: gridState.columnState, applyOrder: true });
        }
        if (gridState.filterModel && api.setFilterModel) {
            api.setFilterModel(gridState.filterModel);
        }
        if (gridState.rowDensity) {
            setRowDensity(gridState.rowDensity);
            localStorage.setItem('payroll_row_density', gridState.rowDensity);
        }
        refreshColumnOptions();
    }, [refreshColumnOptions]);

    const loadGridPreference = useCallback(async () => {
        try {
            setGridPreferenceLoading(true);
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/users/grid-preferences/${PAYROLL_EXECUTION_GRID_KEY}`, {
                params: { view_name: "default" },
                withCredentials: true,
                headers,
            });
            if (res.data?.success && res.data?.data?.grid_state) {
                applyGridPreference(res.data.data.grid_state);
            } else {
                refreshColumnOptions();
            }
        } catch (err) {
            console.error("Failed to load payroll grid preference:", err);
            refreshColumnOptions();
        } finally {
            setGridPreferenceLoading(false);
        }
    }, [applyGridPreference, refreshColumnOptions]);

    const handleSaveGridPreference = useCallback(async () => {
        const api = gridRef.current?.api;
        if (!api?.getColumnState) return;
        try {
            setGridPreferenceSaving(true);
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };
            await axios.post(`${process.env.REACT_APP_API_URL1}/users/grid-preferences/${PAYROLL_EXECUTION_GRID_KEY}`, {
                view_name: "default",
                grid_state: {
                    version: 1,
                    columnState: api.getColumnState(),
                    filterModel: api.getFilterModel ? api.getFilterModel() : null,
                    rowDensity: rowDensity,
                    savedAt: new Date().toISOString(),
                },
            }, {
                withCredentials: true,
                headers,
            });
            setStatusMsg({ open: true, msg: "Payroll execution column view saved successfully.", severity: "success" });
        } catch (err) {
            console.error("Failed to save payroll grid preference:", err);
            setStatusMsg({ open: true, msg: err.response?.data?.message || "Could not save column view.", severity: "error" });
        } finally {
            setGridPreferenceSaving(false);
        }
    }, [rowDensity]);

    const handleResetGridPreference = useCallback(async () => {
        const api = gridRef.current?.api;
        if (!api) return;
        try {
            setGridPreferenceSaving(true);
            if (api.resetColumnState) api.resetColumnState();
            if (api.setFilterModel) api.setFilterModel(null);
            const token = localStorage.getItem('token');
            const tenant = localStorage.getItem('tenant') || '';
            const headers = { Authorization: `Bearer ${token}`, 'X-Tenant': tenant };
            await axios.delete(`${process.env.REACT_APP_API_URL1}/users/grid-preferences/${PAYROLL_EXECUTION_GRID_KEY}`, {
                params: { view_name: "default" },
                withCredentials: true,
                headers,
            });
            refreshColumnOptions();
            setStatusMsg({ open: true, msg: "Column view reset to system default.", severity: "success" });
        } catch (err) {
            console.error("Failed to reset payroll grid preference:", err);
            setStatusMsg({ open: true, msg: err.response?.data?.message || "Could not reset column view.", severity: "error" });
        } finally {
            setGridPreferenceSaving(false);
        }
    }, [refreshColumnOptions]);

    const selectedGroupName = metadata.payrollGroups.find(g => Number(g.id) === Number(filters.payroll_group_id))?.group_name || 'All Groups';
    const selectedMonthName = months.find(m => m.value === filters.month)?.label || '';
    const payrollSummary = useMemo(() => filteredRowData.reduce((summary, row) => {
        const gross = Number(row.gross_pay || 0);
        const net = Number(row.net_pay || 0);
        const explicitDeductions = Number(row.total_deductions || 0);
        const deductions = explicitDeductions || Math.max(0, gross - net);
        const hasException = !row.employee_number || Number(row.basic_salary || 0) <= 0 || net < 0;

        return {
            staff: summary.staff + 1,
            gross: summary.gross + gross,
            deductions: summary.deductions + deductions,
            net: summary.net + net,
            exceptions: summary.exceptions + (hasException ? 1 : 0)
        };
    }, { staff: 0, gross: 0, deductions: 0, net: 0, exceptions: 0 }), [filteredRowData]);

    const readinessGroups = useMemo(() => ({
        errors: readiness?.checks?.filter(check => ['ERROR', 'BLOCKING_ERROR'].includes(check.severity)) || [],
        warnings: readiness?.checks?.filter(check => check.severity === 'WARNING') || [],
        passed: readiness?.checks?.filter(check => check.severity === 'PASS') || []
    }), [readiness]);

    const saveDraftDisabledReason = useMemo(() => {
        if (loading) return 'Payroll calculation is still running.';
        if (rowData.length === 0) return 'Calculate a payroll preview first.';
        if (filters.run_type !== 'REGULAR') return '';
        if (!filters.payroll_group_id) return 'Select a payroll group.';
        if (filters.period_type === 'ADVANCE' && !filters.advance_schedule_id) return 'Select an advance schedule.';
        if (existingRun) return `Run #${existingRun.id} already exists for this period.`;
        if (readiness?.period?.status !== 'LOCKED') return 'Resolve blocking readiness issues and lock the payroll period.';
        return '';
    }, [existingRun, filters, loading, readiness, rowData.length]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f4f7fe', overflow: 'hidden', overscrollBehaviorX: 'none' }}>
            <AppHeader title="Payroll Execution" setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: 2, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', overscrollBehaviorX: 'none', '& > *': { flexShrink: 0 } }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
                    <Box>
                        <Typography variant="h5" sx={{ color: '#0f172a', fontWeight: 900, letterSpacing: '-0.02em' }}>Payroll Execution</Typography>
                        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>Prepare, validate, and save an auditable payroll draft.</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        <Chip label="1  Parameters" color="primary" variant="outlined" sx={{ fontWeight: 800 }} />
                        <Chip label="2  Validate & Lock" color={readiness?.period?.status === 'LOCKED' ? 'success' : 'default'} variant="outlined" sx={{ fontWeight: 800 }} />
                        <Chip label="3  Review & Save Draft" color={rowData.length > 0 ? 'primary' : 'default'} variant="outlined" sx={{ fontWeight: 800 }} />
                    </Stack>
                </Stack>
                {/* SECTION 1: Parameters & Action Bar (Collapsible Accordion) */}
                <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                    <Box sx={{
                        px: 2, py: 1.25,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        bgcolor: '#FFF',
                        borderBottom: accState.params ? '1px solid #F1F5F9' : 'none',
                        flexWrap: 'wrap', gap: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer' }} onClick={() => toggleAccordion('params')}>
                            <IconButton size="small" sx={{ p: 0.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                {accState.params ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                ⚙️ Run Parameters
                                {!accState.params && (
                                    <Chip
                                        label={`${selectedMonthName} ${filters.year} • ${filters.run_type} • ${selectedGroupName} • ${filters.period_type === 'REGULAR' ? 'Month End' : 'Advance'}`}
                                        size="small"
                                        sx={{ ml: 1, bgcolor: '#EEF2FF', color: '#3730A3', fontWeight: 600, fontSize: '0.75rem' }}
                                    />
                                )}
                            </Typography>
                        </Box>

                        {/* Primary actions stay permanently accessible even when parameters are collapsed */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 'auto' }}>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<RunIcon />}
                                onClick={fetchPreview}
                                disabled={loading || (filters.run_type === 'REGULAR' && (!filters.payroll_group_id || (filters.period_type === 'ADVANCE' && !filters.advance_schedule_id)))}
                                sx={{ borderRadius: 1.5, textTransform: 'none', bgcolor: '#1a237e', fontWeight: 600, px: 1.75 }}
                            >
                                Calculate Preview
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<UploadIcon />}
                                onClick={() => navigate('/payroll/adhoc')}
                                disabled={loading}
                                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                            >
                                Ad-Hoc Variables
                            </Button>
                            {rowData.length > 0 && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<DownloadIcon />}
                                    onClick={onBtnExport}
                                    disabled={loading}
                                    sx={{
                                        borderRadius: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        color: '#10b981',
                                        borderColor: '#10b981',
                                        '&:hover': { borderColor: '#059669', bgcolor: '#f0fdf4' }
                                    }}
                                >
                                    Export to Excel
                                </Button>
                            )}
                            <Tooltip title={saveDraftDisabledReason || 'Save this preview as a draft payroll run.'}>
                                <span>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<SaveIcon />}
                                        onClick={createRun}
                                        disabled={Boolean(saveDraftDisabledReason)}
                                        color="success"
                                        sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Save Draft Run
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </Box>

                    {/* Collapsible Parameter Dropdowns */}
                    <Collapse in={accState.params}>
                        <Box sx={{ p: 2, bgcolor: '#FAFAFA' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(150px, 1fr))' }, gap: 2, alignItems: 'center' }}>
                                <Box>
                                    <TextField
                                        select fullWidth label="Month" size="small"
                                        value={filters.month}
                                        onChange={(e) => { setFilters({ ...filters, month: e.target.value }); setExistingRun(null); setReadiness(null); setRowData([]); }}
                                    >
                                        {months.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                    </TextField>
                                </Box>
                                <Box>
                                    <TextField
                                        select fullWidth label="Year" size="small"
                                        value={filters.year}
                                        onChange={(e) => { setFilters({ ...filters, year: e.target.value }); setExistingRun(null); setReadiness(null); setRowData([]); }}
                                    >
                                        {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                    </TextField>
                                </Box>
                                <Box>
                                    <TextField
                                        select fullWidth label="Run Type" size="small"
                                        value={filters.run_type}
                                        onChange={(e) => { setFilters({ ...filters, run_type: e.target.value, off_cycle_elements: [] }); setExistingRun(null); setReadiness(null); setRowData([]); }}
                                    >
                                        <MenuItem value="REGULAR">Regular</MenuItem>
                                        <MenuItem value="OFF_CYCLE">Off-Cycle</MenuItem>
                                    </TextField>
                                </Box>
                                {filters.run_type === 'REGULAR' && (
                                    <>
                                        <Box>
                                            <TextField select fullWidth label="Payroll Group" size="small" value={filters.payroll_group_id}
                                                onChange={(e) => setFilters({ ...filters, payroll_group_id: e.target.value, advance_schedule_id: '' })}>
                                                {metadata.payrollGroups.map(group => <MenuItem key={group.id} value={group.id}>{group.group_name}</MenuItem>)}
                                            </TextField>
                                        </Box>
                                        <Box>
                                            <TextField select fullWidth label="Period Type" size="small" value={filters.period_type}
                                                onChange={(e) => setFilters({ ...filters, period_type: e.target.value, advance_schedule_id: '' })}>
                                                <MenuItem value="REGULAR">Regular / Month End</MenuItem>
                                                <MenuItem value="ADVANCE">Advance</MenuItem>
                                            </TextField>
                                        </Box>
                                        {filters.period_type === 'ADVANCE' && (
                                            <Box>
                                                <TextField select fullWidth label="Advance Schedule" size="small" value={filters.advance_schedule_id}
                                                    onChange={(e) => setFilters({ ...filters, advance_schedule_id: e.target.value })}>
                                                    {(metadata.payrollGroups.find(group => Number(group.id) === Number(filters.payroll_group_id))?.schedules || [])
                                                        .filter(schedule => Number(schedule.is_active) === 1)
                                                        .map(schedule => <MenuItem key={schedule.id} value={schedule.id}>{schedule.schedule_name}</MenuItem>)}
                                                </TextField>
                                            </Box>
                                        )}
                                    </>
                                )}
                                {filters.run_type === 'OFF_CYCLE' && (
                                    <Box sx={{ gridColumn: { lg: 'span 2' } }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="off-cycle-elements-label">Off-Cycle Elements</InputLabel>
                                            <Select
                                                labelId="off-cycle-elements-label"
                                                multiple
                                                value={filters.off_cycle_elements}
                                                onChange={(e) => setFilters({ ...filters, off_cycle_elements: e.target.value })}
                                                input={<OutlinedInput label="Off-Cycle Elements" />}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const el = elements.find(e => e.id === value);
                                                            return <Chip key={value} label={el ? el.element_name : value} size="small" />;
                                                        })}
                                                    </Box>
                                                )}
                                            >
                                                {elements.map((el) => (
                                                    <MenuItem key={el.id} value={el.id}>
                                                        <Checkbox checked={filters.off_cycle_elements.indexOf(el.id) > -1} />
                                                        <ListItemText primary={el.element_name} />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Collapse>
                </Card>

                {/* SECTION 2: Payroll Readiness (Collapsible Accordion) */}
                {filters.run_type === 'REGULAR' && readiness && (
                    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                        <Box
                            sx={{
                                px: 2, py: 1.25,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                bgcolor: '#FFF',
                                borderBottom: accState.readiness ? '1px solid #F1F5F9' : 'none',
                                cursor: 'pointer',
                                flexWrap: 'wrap', gap: 1
                            }}
                            onClick={() => toggleAccordion('readiness')}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <IconButton size="small" sx={{ p: 0.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                                    {accState.readiness ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                    🛡️ Payroll Readiness — {readiness.startDate} to {readiness.endDate}
                                </Typography>
                                <Chip
                                    label={readiness.period?.status || 'OPEN'}
                                    color={readiness.period?.status === 'LOCKED' ? 'success' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 700, height: 22 }}
                                />
                                {!accState.readiness && (
                                    <Stack direction="row" spacing={0.5} sx={{ ml: 0.5 }}>
                                        {readiness.checks.filter(c => ['ERROR', 'BLOCKING_ERROR'].includes(c.severity)).length > 0 && (
                                            <Chip
                                                label={`${readiness.checks.filter(c => ['ERROR', 'BLOCKING_ERROR'].includes(c.severity)).length} Issues`}
                                                color="error"
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                            />
                                        )}
                                        {readiness.checks.filter(c => c.severity === 'WARNING').length > 0 && (
                                            <Chip
                                                label={`${readiness.checks.filter(c => c.severity === 'WARNING').length} Warnings`}
                                                color="warning"
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                            />
                                        )}
                                        {readiness.checks.filter(c => c.severity === 'PASS').length > 0 && (
                                            <Chip
                                                label={`${readiness.checks.filter(c => c.severity === 'PASS').length} Passed`}
                                                color="success"
                                                variant="outlined"
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </Box>

                            <Stack direction="row" spacing={1} alignItems="center" onClick={e => e.stopPropagation()}>
                                {['LOCKED', 'PAYROLL_DRAFTED'].includes(readiness.period?.status) ? (
                                    <Button size="small" color="warning" variant="outlined" sx={{ borderRadius: 1.5, py: 0.25 }} onClick={() => changePeriodLock(false)}>
                                        Reopen Period
                                    </Button>
                                ) : (
                                    <Tooltip title={readiness.canProceed ? 'Lock this period before saving the payroll draft.' : `${readinessGroups.errors.length} blocking readiness issue${readinessGroups.errors.length === 1 ? '' : 's'} must be resolved first.`}>
                                        <span>
                                            <Button size="small" variant="contained" startIcon={<LockIcon />} disabled={!readiness.canProceed} sx={{ borderRadius: 1.5, py: 0.25 }} onClick={() => changePeriodLock(true)}>
                                                Lock Period
                                            </Button>
                                        </span>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>

                        <Collapse in={accState.readiness}>
                            <Box sx={{ p: 1.5, bgcolor: '#FAFAFA', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                                <ReadinessGroup title="Blocking Issues" checks={readinessGroups.errors} severity="ERROR" onOpenDetails={check => setDetailsDialog({ open: true, title: check.message, details: check.details })} />
                                <ReadinessGroup title="Warnings" checks={readinessGroups.warnings} severity="WARNING" onOpenDetails={check => setDetailsDialog({ open: true, title: check.message, details: check.details })} />
                                <ReadinessGroup title="Passed Checks" checks={readinessGroups.passed} severity="PASS" onOpenDetails={check => setDetailsDialog({ open: true, title: check.message, details: check.details })} />
                            </Box>
                        </Collapse>
                    </Card>
                )}

                {filters.run_type === 'REGULAR' && existingRun && (
                    <Alert
                        severity="warning"
                        sx={{ borderRadius: 2 }}
                        action={<Button color="inherit" size="small" onClick={() => navigate('/payroll/draft-review')}>View Existing Run</Button>}
                    >
                        A regular payroll run already exists for {String(filters.month).padStart(2, '0')}/{filters.year}: Run #{existingRun.id} — {existingRun.status}. Saving another draft is disabled.
                    </Alert>
                )}

                {rowData.length > 0 && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' }, gap: 1.25 }}>
                        <SummaryMetric icon={PeopleIcon} label="Employees" value={payrollSummary.staff} helper={payrollSummary.exceptions > 0 ? `${payrollSummary.exceptions} need attention` : 'Preview population'} />
                        <SummaryMetric icon={PaymentsIcon} label="Gross Payroll" value={`${money(payrollSummary.gross)} MMK`} tone="primary" />
                        <SummaryMetric icon={DeductionIcon} label="Deductions" value={`${money(payrollSummary.deductions)} MMK`} tone={payrollSummary.deductions > 0 ? 'danger' : 'neutral'} />
                        <SummaryMetric icon={NetPayIcon} label="Net Payroll" value={`${money(payrollSummary.net)} MMK`} tone="success" />
                        <SummaryMetric
                            icon={readinessGroups.errors.length > 0 || payrollSummary.exceptions > 0 ? WarningIcon : PassedIcon}
                            label="Preview Status"
                            value={readinessGroups.errors.length > 0 ? `${readinessGroups.errors.length} blockers` : payrollSummary.exceptions > 0 ? `${payrollSummary.exceptions} exceptions` : 'Ready to review'}
                            tone={readinessGroups.errors.length > 0 || payrollSummary.exceptions > 0 ? 'danger' : 'success'}
                        />
                    </Box>
                )}

                {/* SECTION 3: View Filters & Totals Bar (Collapsible with Column Controls) */}
                {rowData.length > 0 && (
                    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 10px 0 rgba(0,0,0,0.04)', border: '1px solid #E2E8F0', overflow: 'hidden' }}>
                        <Box sx={{
                            px: 2, py: 1.25,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            bgcolor: '#FFF',
                            borderBottom: accState.filters ? '1px solid #F1F5F9' : 'none',
                            flexWrap: 'wrap', gap: 1
                        }}>
                            {/* Left: Totals KPI & Filter toggle */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                <Button
                                    size="small"
                                    variant={accState.filters ? "contained" : "outlined"}
                                    color={accState.filters ? "primary" : "inherit"}
                                    startIcon={<FilterListIcon />}
                                    onClick={() => toggleAccordion('filters')}
                                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                                >
                                    {accState.filters ? "Hide Filters" : "Filter Staff"}
                                </Button>

                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#64748b' }}>
                                    Showing {filteredRowData.length} of {rowData.length} employees
                                </Typography>
                            </Box>

                            {/* Right: Density Toggle, Column Visibility, Save View, Reset View */}
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Tooltip title={`Row Density: ${rowDensity === 'compact' ? 'Compact 36px (Click for Standard 44px)' : 'Standard 44px (Click for Compact 36px)'}`}>
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        size="small"
                                        startIcon={<DensityIcon fontSize="small" />}
                                        onClick={() => {
                                            const next = rowDensity === 'compact' ? 'standard' : 'compact';
                                            setRowDensity(next);
                                            localStorage.setItem('payroll_row_density', next);
                                            setTimeout(() => {
                                                gridRef.current?.api?.resetRowHeights();
                                            }, 0);
                                        }}
                                        sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                                    >
                                        {rowDensity === 'compact' ? 'Compact (36px)' : 'Standard (44px)'}
                                    </Button>
                                </Tooltip>

                                <Tooltip title="Show or hide payroll columns by category">
                                    <Button
                                        variant="outlined"
                                        color="inherit"
                                        size="small"
                                        startIcon={<ViewColumnIcon />}
                                        onClick={() => {
                                            refreshColumnOptions();
                                            setColumnDialogOpen(true);
                                        }}
                                        sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                                    >
                                        Columns
                                    </Button>
                                </Tooltip>

                                <Tooltip title="Save current column visibility, widths, order, and filters">
                                    <span>
                                        <Button
                                            variant="outlined"
                                            color="inherit"
                                            size="small"
                                            startIcon={<SaveIcon />}
                                            disabled={gridPreferenceSaving}
                                            onClick={handleSaveGridPreference}
                                            sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600, py: 0.5 }}
                                        >
                                            Save View
                                        </Button>
                                    </span>
                                </Tooltip>

                                <Tooltip title="Reset to default columns and order">
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={handleResetGridPreference}
                                            disabled={gridPreferenceSaving}
                                            sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', p: 0.6 }}
                                        >
                                            <RestartAltIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Box>

                        {/* Collapsible Filter Inputs */}
                        <Collapse in={accState.filters}>
                            <Box sx={{ p: 1.5, bgcolor: '#FAFAFA' }}>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(180px, 1fr))' }, gap: 1.5, alignItems: 'center' }}>
                                    <Box>
                                        <TextField
                                            fullWidth label="Search Name / ID" size="small"
                                            value={viewFilters.searchText}
                                            onChange={(e) => setViewFilters({ ...viewFilters, searchText: e.target.value })}
                                        />
                                    </Box>
                                    <Box>
                                        <TextField
                                            select fullWidth label="Branch" size="small"
                                            value={viewFilters.branchId}
                                            onChange={(e) => setViewFilters({ ...viewFilters, branchId: e.target.value })}
                                        >
                                            <MenuItem value="">All Branches</MenuItem>
                                            {metadata.branches.map(b => <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>)}
                                        </TextField>
                                    </Box>
                                    <Box>
                                        <TextField
                                            select fullWidth label="Org Node" size="small"
                                            value={viewFilters.orgNodeId}
                                            onChange={(e) => setViewFilters({ ...viewFilters, orgNodeId: e.target.value })}
                                        >
                                            <MenuItem value="">All Nodes</MenuItem>
                                            {metadata.nodes.map(n => <MenuItem key={n.id} value={n.id}>{n.node_name}</MenuItem>)}
                                        </TextField>
                                    </Box>
                                    <Box>
                                        <TextField
                                            select fullWidth label="Grade" size="small"
                                            value={viewFilters.gradeId}
                                            onChange={(e) => setViewFilters({ ...viewFilters, gradeId: e.target.value })}
                                        >
                                            <MenuItem value="">All Grades</MenuItem>
                                            {metadata.grades.map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                                        </TextField>
                                    </Box>
                                </Box>
                            </Box>
                        </Collapse>
                    </Card>
                )}

                {loading && <LinearProgress sx={{ borderRadius: 1 }} />}

                {/* AG GRID CARD (Dynamic flex height fills remaining screen) */}
                <Card sx={{
                    borderRadius: 2,
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
                    border: '1px solid #E2E8F0',
                    flexGrow: 1,
                    minHeight: 320,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {rowData.length === 0 ? (
                        <Box sx={{ flexGrow: 1, minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center', bgcolor: '#fff' }}>
                            <Box sx={{ maxWidth: 480 }}>
                                <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: '#eef2ff', color: '#3730a3', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}><RunIcon /></Box>
                                <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 900 }}>Calculate a payroll preview</Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.75 }}>Choose the payroll period and group, then calculate a preview to validate readiness, inspect employee results, and save a draft.</Typography>
                                <Button variant="contained" startIcon={<RunIcon />} onClick={fetchPreview} disabled={loading || (filters.run_type === 'REGULAR' && !filters.payroll_group_id)} sx={{ mt: 2.5, textTransform: 'none', fontWeight: 800 }}>Calculate Preview</Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            className="ag-theme-quartz"
                            sx={{
                                width: '100%',
                                height: '100%',
                                flexGrow: 1,
                                overscrollBehaviorX: 'none',
                                '& .ag-header-cell-label': { fontSize: '0.8125rem' },
                                '& .ag-header-group-cell-label': { fontSize: '0.8125rem', fontWeight: 700 },
                                '& .ag-cell': { fontSize: '0.8125rem', display: 'flex', alignItems: 'center' },
                                '& .payroll-row-exception': { bgcolor: '#fff7ed' }
                            }}
                        >
                            <AgGridReact
                                theme="legacy"
                                ref={gridRef}
                                rowData={filteredRowData}
                                columnDefs={columnDefs}
                                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                                pagination={true}
                                paginationPageSize={20}
                                paginationPageSizeSelector={[10, 20, 50, 100]}
                                animateRows={true}
                                rowHeight={rowDensity === 'compact' ? 36 : 44}
                                headerHeight={rowDensity === 'compact' ? 36 : 44}
                                groupHeaderHeight={rowDensity === 'compact' ? 34 : 40}
                                rowClassRules={{ 'payroll-row-exception': params => !params.data?.employee_number || Number(params.data?.basic_salary || 0) <= 0 || Number(params.data?.net_pay || 0) < 0 }}
                                onGridReady={() => {
                                    loadGridPreference();
                                }}
                                onColumnMoved={refreshColumnOptions}
                                onColumnVisible={refreshColumnOptions}
                            />
                        </Box>
                    )}
                </Card>
            </Box>

            {/* GROUPED COLUMN VISIBILITY DIALOG */}
            <Dialog
                open={columnDialogOpen}
                onClose={() => setColumnDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700}>Payroll Column Visibility</Typography>
                    <Chip
                        label={`${groupedColumns.reduce((sum, g) => sum + g.items.filter(i => i.visible).length, 0)} visible`}
                        size="small"
                        color="primary"
                    />
                </DialogTitle>
                <DialogContent dividers sx={{ p: 2 }}>
                    {gridPreferenceLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary">Loading saved view...</Typography>
                        </Box>
                    ) : (
                        <Stack spacing={2}>
                            {groupedColumns.map(group => {
                                const allVisible = group.items.every(i => i.visible);

                                return (
                                    <Box key={group.id} sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>
                                                {group.title} ({group.items.filter(i => i.visible).length}/{group.items.length})
                                            </Typography>
                                            <Button
                                                size="small"
                                                onClick={() => handleToggleGroup(group.items, !allVisible)}
                                                sx={{ textTransform: 'none', py: 0.25, fontSize: '0.75rem', fontWeight: 600 }}
                                            >
                                                {allVisible ? "Hide All" : "Show All"}
                                            </Button>
                                        </Box>
                                        <Grid container spacing={0.5}>
                                            {group.items.map(item => (
                                                <Grid item xs={12} sm={6} key={item.colId}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                size="small"
                                                                checked={item.visible}
                                                                onChange={e => handleToggleColumn(item.colId, e.target.checked)}
                                                            />
                                                        }
                                                        label={<Typography variant="body2">{item.label}</Typography>}
                                                        sx={{ m: 0 }}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1.5 }}>
                    <Button onClick={() => setColumnDialogOpen(false)} color="inherit">Close</Button>
                    <Button
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        disabled={gridPreferenceSaving}
                        onClick={handleResetGridPreference}
                    >
                        Reset Default
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={gridPreferenceSaving}
                        onClick={() => {
                            handleSaveGridPreference();
                            setColumnDialogOpen(false);
                        }}
                        color="primary"
                    >
                        Save View
                    </Button>
                </DialogActions>
            </Dialog>


            <Snackbar
                open={statusMsg.open}
                autoHideDuration={6000}
                onClose={() => setStatusMsg({ ...statusMsg, open: false })}
            >
                <Alert severity={statusMsg.severity} sx={{ width: '100%' }}>
                    {statusMsg.msg}
                </Alert>
            </Snackbar>

            <Dialog open={detailsDialog.open} onClose={() => setDetailsDialog({ ...detailsDialog, open: false })} maxWidth="sm" fullWidth>
                <DialogTitle>{detailsDialog.title}</DialogTitle>
                <DialogContent dividers>
                    <List dense>
                        {detailsDialog.details.map((staff, idx) => (
                            <ListItem key={staff.id || idx}>
                                <ListItemText primary={staff.full_name} secondary={`Staff ID: ${staff.staff_code || staff.id}`} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog({ ...detailsDialog, open: false })}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!breakdownPayroll} onClose={() => setBreakdownPayroll(null)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    Payroll Calculation — {breakdownPayroll?.full_name || breakdownPayroll?.employee_number || ''}
                </DialogTitle>
                <DialogContent dividers>
                    <EffectiveDatedPayrollBreakdown payroll={breakdownPayroll} />
                </DialogContent>
                <DialogActions><Button onClick={() => setBreakdownPayroll(null)}>Close</Button></DialogActions>
            </Dialog>

        </Box>
    );
};

export default PayrollProcessing;
